/**
 * Hook for AI Recommended Actions
 * Fetches proactive action items with AI-drafted content
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../../../utils/api';

export interface ActionItem {
  id: string;
  type: 'reply_email' | 'review_document' | 'follow_up' | 'complete_task' | 'prepare_meeting';
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  subtitle: string;
  estimated_time: string;
  ai_ready: boolean;
  ai_draft?: string;
  ai_summary?: string;
  
  // References
  email_id?: string;
  case_id?: string;
  case_title?: string;
  client_name?: string;
  document_id?: string;
  task_id?: string;
  meeting_id?: string;
  
  // Email-specific
  email_from?: string;
  email_to?: string;
  email_subject?: string;
  email_body_preview?: string;
  
  // Action metadata
  action_label: string;
  action_url?: string;
  created_at?: string;
}

interface UseRecommendedActionsResult {
  actions: ActionItem[];
  isLoading: boolean;
  error: string | null;
  summary: string | null;
  refetch: () => Promise<void>;
  executeAction: (actionId: string, data: any) => Promise<void>;
  dismissAction: (actionId: string) => void;
}

export const useRecommendedActions = (language: string = 'no'): UseRecommendedActionsResult => {
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const fetchActions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        return;
      }
      
      const res = await axios.get(`${API_URL}/ai/recommended-actions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { lang: language }
      });
      
      // Filter out dismissed
      const filtered = (res.data.actions || []).filter(
        (a: ActionItem) => !dismissedIds.has(a.id)
      );
      
      setActions(filtered);
      setSummary(res.data.summary || null);
    } catch (err) {
      console.error('Failed to fetch actions:', err);
      setError(language === 'en' ? 'Could not load actions' : 'Kunne ikke hente handlinger');
      setActions([]);
    } finally {
      setIsLoading(false);
    }
  }, [dismissedIds, language]);

  useEffect(() => {
    fetchActions();
  }, [language]);

  const executeAction = useCallback(async (actionId: string, data: any) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/ai/recommended-actions/${actionId}/execute`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Remove from list after execution
      setActions(prev => prev.filter(a => a.id !== actionId));
    } catch (err) {
      console.error('Failed to execute action:', err);
      throw err;
    }
  }, []);

  const dismissAction = useCallback((actionId: string) => {
    setDismissedIds(prev => new Set([...prev, actionId]));
    setActions(prev => prev.filter(a => a.id !== actionId));
  }, []);

  return {
    actions,
    isLoading,
    error,
    summary,
    refetch: fetchActions,
    executeAction,
    dismissAction
  };
};
